/**
 * useKeyboardShortcuts — V1035-3-2.
 *
 * Registers global keyboard shortcuts and provides a registry for the
 * help overlay. Shortcuts are disabled when a text input or textarea
 * is focused (except modifier combos like Cmd+Enter).
 */

import { useEffect, useRef } from 'react';
import { useDialogMobileOnly } from './use-dialog-mobile-only.js';

export interface Shortcut {
  key: string;
  label: string;
  modifier?: 'cmd' | 'ctrl' | 'meta';
  action: () => void;
}

function isTextInput(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type);
  }
  return (el as HTMLElement).isContentEditable;
}

export function useKeyboardShortcuts(shortcuts: Shortcut[]): void {
  const ref = useRef(shortcuts);
  ref.current = shortcuts;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      for (const s of ref.current) {
        const needsMod = !!s.modifier;
        const modPressed = e.metaKey || e.ctrlKey;
        if (needsMod && !modPressed) continue;
        if (!needsMod && isTextInput(document.activeElement)) continue;
        if (e.key.toLowerCase() === s.key.toLowerCase() || (s.key === '?' && e.key === '?')) {
          if (needsMod ? modPressed : true) {
            e.preventDefault();
            s.action();
            return;
          }
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
}

export function ShortcutHelpOverlay({ shortcuts, onClose }: { shortcuts: Shortcut[]; onClose: () => void }) {
  const dialogRef = useDialogMobileOnly<HTMLDivElement>(true);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={dialogRef} aria-label="Keyboard shortcuts" style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
        padding: '24px', minWidth: 280, maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>Keyboard shortcuts</h2>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
          {shortcuts.map((s) => (
            <li key={s.key + (s.modifier ?? '')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>{s.label}</span>
              <kbd style={{
                background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: '4px',
                padding: '2px 8px', fontSize: '0.8125rem', fontFamily: 'var(--font-mono)', color: 'var(--text)',
              }}>
                {s.modifier ? `${s.modifier === 'cmd' ? '⌘' : 'Ctrl'}+` : ''}{s.key === '?' ? '?' : s.key}
              </kbd>
            </li>
          ))}
        </ul>
        <p style={{ margin: '16px 0 0', fontSize: '0.75rem', color: 'var(--text-dim)' }}>Press Esc to close</p>
      </div>
    </div>
  );
}
